import React from 'react';
import { Button, Progress, TableColumnType, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { Code, CountBadge, Image, Paragraph } from 'common/components';
import { countFormatter, dateFormatter, phoneFormatter, priceFormatter } from 'common/helpers/formatter.helper';
import {
  DEFAULT_LIST_OFFSET,
  DEFAULT_VALUE_0,
  LIST_LIMIT_20,
  PERCENT_100,
  PERCENT_50,
  VISIBLE_IMAGES_LIMIT_3,
  VISIBLE_IMAGES_LIMIT_4,
} from 'common/config';
import { getEmail, getPriceToDisplay } from 'common/helpers';
import { IAccountShort, IOption, ISupplyShort } from 'common/models';
import { EOrderDirection, EOrderField, ERoute } from 'common/const';
import { ArrowRightLong, InfoGrey, InfoRed } from 'common/svg';
import { getIsSelfRegistered } from 'entities/Account/Account.helper';
import {
  IRequest,
  IRequestListPayload,
  IRequestDistribution,
  IRequestFilters,
  IRequestListItem,
  IRequestMembers,
  IRequestStats,
  IRequestSupplyGroupItem,
} from 'entities/Request/Request.models';
import {
  ERequestListPageTab,
  ERequestStatus,
  ERequestSupplyGroupKey,
  ERequestSupplyGroupType,
} from 'entities/Request/Request.enum';
import { SupplyStatus } from 'entities/Supply/components/SupplyStatus';
import { getUserName, getUserRole } from 'entities/User/User.helper';
import {
  excludeSuppliesByStatuses,
  filterSuppliesByStatuses,
  getSuppliesCount,
  getSupplyFormattedDate,
  getSupplyStatus,
} from 'entities/Supply/Supply.helper';
import { ESupplyStatus, ESupplyStatusLabel } from 'entities/Supply/Supply.enum';
import { EUserRole } from 'entities/User/User.enum';
import { IUser } from 'entities/User/User.models';
import { EDeliveryType } from 'entities/Basket/Basket.models';

export const getRequestListPageTabs = (role?: string) => {
  return [
    {
      label: role === EUserRole.User ? 'Мои заказы' : 'Контроль заказов',
      path: `${ERoute.RequestList}/${role}/${ERequestListPageTab.Requests}`,
    },
    {
      label: 'График поставок',
      path: `${ERoute.RequestList}/${role}/${ERequestListPageTab.Supplies}`,
    },
  ];
};

export const getRequestListTabs = (stats: IRequestStats, role?: string) => {
  const asSellerManager = role === EUserRole.SellerManager;

  if (asSellerManager) {
    return [
      {
        key: ERequestStatus.Processing,
        label: (
          <>
            Входящие заказы
            <CountBadge count={stats.processingCount} />
          </>
        ),
      },
      {
        key: ERequestStatus.Cancelled,
        label: 'Завершенные заказы',
      },
    ];
  }

  return [
    {
      key: ERequestStatus.Created,
      label: (
        <>
          На согласовании
          <CountBadge count={stats.createdCount} />
        </>
      ),
    },
    {
      key: ERequestStatus.Processing,
      label: (
        <>
          Размещенные заказы
          <CountBadge count={stats.processingCount} />
        </>
      ),
    },
    {
      key: ERequestStatus.Cancelled,
      label: 'Завершенные заказы',
    },
  ];
};

export const getRequestListPayloadStatuses = (role: string, tab?: string) => {
  switch (tab) {
    case ERequestStatus.Created: {
      return [ERequestStatus.Created];
    }
    case ERequestStatus.Processing: {
      return [ERequestStatus.Processing];
    }
    case ERequestStatus.Cancelled: {
      return [ERequestStatus.Delivered, ERequestStatus.Cancelled];
    }
    default: {
      if (role === EUserRole.SellerManager) {
        return [ERequestStatus.Processing];
      }

      return [ERequestStatus.Created];
    }
  }
};

export const getRequestListPayload = (role: string, tab?: string) => {
  const asUser = role === EUserRole.User;
  const asSellerManager = role === EUserRole.SellerManager;

  const payload: IRequestListPayload = {
    statuses: getRequestListPayloadStatuses(role, tab),
    myRequests: asUser,
    limit: LIST_LIMIT_20,
    offset: DEFAULT_LIST_OFFSET,
    ...(asSellerManager &&
      (!tab || tab === ERequestStatus.Processing) && {
        orderField: EOrderField.UpdatedAt,
        orderDirection: EOrderDirection.DESC,
      }),
  };

  return payload;
};

const getRequestSupplyDateTypeItem = (key: string, label: string, items?: ISupplyShort[]) => {
  return {
    key,
    type: ERequestSupplyGroupType.Date,
    label,
    ids: items?.map((item) => item.id),
  };
};

const getRequestSupplyDataTypeItem = (key: string, item: ISupplyShort) => {
  return {
    key: item.id,
    type: ERequestSupplyGroupType.Data,
    group: key,
    data: item,
  };
};

export const getRequestIdForView = (id: number, createdAt: string) => {
  return `#${dateFormatter('YYYYMMDD', createdAt)}-${id}`;
};

export const getRequestStatus = (status?: string) => {
  return {
    isCreatedRequest: status === ERequestStatus.Created,
    isProcessingRequest: status === ERequestStatus.Processing,
    isDeliveredRequest: status === ERequestStatus.Delivered,
    isCancelledRequest: status === ERequestStatus.Cancelled,
  };
};

export const getRequestSuppliesByStatus = (request: IRequest | null) => {
  const supplies = request?.supplies || [];

  return {
    pendingSupplies: filterSuppliesByStatuses(supplies, [ESupplyStatus.Pending]),
    cancelledSupplies: filterSuppliesByStatuses(supplies, [ESupplyStatus.Cancelled]),
  };
};

export const getRequestProgressSteps = (request: IRequest | null, totalSteps: number) => {
  const supplies = request?.supplies || [];

  const suppliesInDelivery = filterSuppliesByStatuses(supplies, [ESupplyStatus.Approved, ESupplyStatus.Delivery]);
  const suppliesInDeliveryCount = getSuppliesCount(suppliesInDelivery);

  const deliveredSupplies = filterSuppliesByStatuses(supplies, [ESupplyStatus.Delivered]);
  const deliveredSuppliesCount = getSuppliesCount(deliveredSupplies);

  const notAssignedSupplies = filterSuppliesByStatuses(supplies, [ESupplyStatus.Processing]);
  const notAssignedSuppliesCount = getSuppliesCount(notAssignedSupplies);

  const totalSuppliesCount = suppliesInDeliveryCount + deliveredSuppliesCount + notAssignedSuppliesCount;

  const inDeliverySteps = Math.round((suppliesInDeliveryCount * totalSteps) / totalSuppliesCount);
  const deliveredSteps = Math.round((deliveredSuppliesCount * totalSteps) / totalSuppliesCount);
  const notAssignedSteps = totalSteps - inDeliverySteps - deliveredSteps;

  return [
    {
      key: 'in_delivery',
      label: 'В доставке',
      steps: inDeliverySteps,
      count: suppliesInDeliveryCount,
      color: '#F8D247',
    },
    {
      key: 'delivered',
      label: 'Доставлено',
      steps: deliveredSteps,
      count: deliveredSuppliesCount,
      color: '#69A443',
    },
    {
      key: 'not_assigned',
      label: 'Поставка не назначена',
      steps: notAssignedSteps,
      count: notAssignedSuppliesCount,
      color: '#E8EAED',
    },
  ];
};

export const getRequestDetailsGeneralInfo = (request: IRequest, account?: IAccountShort, isSeller?: boolean) => {
  const isSelfRegistered = getIsSelfRegistered(account);
  const filteredSupplies = excludeSuppliesByStatuses(request.supplies, [ESupplyStatus.Cancelled]);
  const count = filteredSupplies.reduce((acc: number, supply) => acc + supply.count, 0);
  const amount = filteredSupplies.reduce((acc: number, supply) => acc + supply.count * supply.price, 0);
  let amountWithTaxes = filteredSupplies.reduce((acc: number, supply) => acc + supply.count * supply.priceWithTaxes, 0);
  let amountVat = amountWithTaxes - amount;

  if ((isSelfRegistered || isSeller) && request.deliveryType === EDeliveryType.Delivery) {
    amountVat += request.deliveryPriceWithTaxes - request.deliveryPrice;
    amountWithTaxes += request.deliveryPriceWithTaxes;
  }

  return {
    count,
    amount,
    amountWithTaxes,
    amountVat,
  };
};

export const getRequestGoodsCardInfo = (supply: ISupplyShort | null) => {
  if (!supply) {
    return { count: DEFAULT_VALUE_0, amount: DEFAULT_VALUE_0, amountWithTaxes: DEFAULT_VALUE_0 };
  }

  return {
    count: supply.count,
    amount: supply.count * supply.price,
    amountWithTaxes: supply.count * supply.priceWithTaxes,
  };
};

export const getRequestSuppliesStatusFilterOptions = (request: IRequest | null) => {
  const statuses = request?.supplies.map((supply) => supply.status);
  const options: IOption[] = [];

  Object.values(ESupplyStatus).forEach((status) => {
    if (statuses?.includes(status)) {
      options.push({
        label: ESupplyStatusLabel[status],
        value: status,
      });
    }
  });

  return options;
};

export const getRequestDataSourceSupplies = (dataSource: IRequestSupplyGroupItem[]) => {
  return dataSource.filter((group) => group.type === ERequestSupplyGroupType.Data).map((group) => group.data as ISupplyShort);
};

export const getRequestSuppliesCheckAll = (supplies: ISupplyShort[], selectedSupplies: ISupplyShort[]) => {
  const filteredSupplies = excludeSuppliesByStatuses(supplies, [ESupplyStatus.Delivered, ESupplyStatus.Cancelled]);

  return !!selectedSupplies.length && selectedSupplies.length === filteredSupplies.length;
};

export const checkUniformStatusForRequestSupplies = (supplies: ISupplyShort[]) => {
  const firstStatus = supplies[0]?.status;

  return {
    isUniformStatus: supplies.every((supply) => supply.status === firstStatus),
    uniformStatus: firstStatus,
  };
};

export const groupRequestSuppliesByDeliveryDateAndStatus = (
  request: IRequest | null,
  filters: IRequestFilters,
): IRequestSupplyGroupItem[] => {
  const filteredSupplies = request?.supplies.filter((supply) => {
    const matchesBySearch = filters.search.length
      ? supply.good.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        supply.good.buyerCode?.toLowerCase().includes(filters.search.toLowerCase())
      : true;
    const matchesByStatus = filters.statuses.length ? filters.statuses.includes(supply.status) : true;

    return matchesBySearch && matchesByStatus;
  });
  const { isCreatedRequest, isProcessingRequest, isDeliveredRequest, isCancelledRequest } = getRequestStatus(request?.status);
  const map = new Map<string, ISupplyShort[]>();

  filteredSupplies?.forEach((supply) => {
    const { isDeliveredSupply, isCancelledSupply } = getSupplyStatus(supply.status);
    let key: string = supply.status;

    if (isCreatedRequest && isCancelledSupply) {
      key = ERequestSupplyGroupKey.Created;
    }

    if (isProcessingRequest && isCancelledSupply) {
      key = ERequestSupplyGroupKey.Processing;
    }

    if (supply.deliveryDate && !isCancelledSupply) {
      key = supply.deliveryDate as string;
    }

    if (isDeliveredRequest || isDeliveredSupply) {
      key = ERequestSupplyGroupKey.Delivered;
    }

    if (isCancelledRequest) {
      key = ERequestSupplyGroupKey.Cancelled;
    }

    if (!map.has(key)) {
      map.set(key, []);
    }

    map.get(key)?.push(supply);
  });

  const result: IRequestSupplyGroupItem[] = [];

  if (map.has(ERequestSupplyGroupKey.Created)) {
    const items = map.get(ERequestSupplyGroupKey.Created);

    result.push(getRequestSupplyDateTypeItem(ERequestSupplyGroupKey.Created, 'Требуется подтверждение', items));
    items?.forEach((item) => {
      result.push(getRequestSupplyDataTypeItem(ERequestSupplyGroupKey.Created, item));
    });
  }

  if (map.has(ERequestSupplyGroupKey.Pending)) {
    const items = map.get(ERequestSupplyGroupKey.Pending);

    result.push(getRequestSupplyDateTypeItem(ERequestSupplyGroupKey.Pending, 'Согласовано', items));
    items?.forEach((item) => {
      result.push(getRequestSupplyDataTypeItem(ERequestSupplyGroupKey.Pending, item));
    });
  }

  if (map.has(ERequestSupplyGroupKey.Processing)) {
    const items = map.get(ERequestSupplyGroupKey.Processing);

    result.push(getRequestSupplyDateTypeItem(ERequestSupplyGroupKey.Processing, 'Дата доставки не назначена', items));
    items?.forEach((item) => {
      result.push(getRequestSupplyDataTypeItem(ERequestSupplyGroupKey.Processing, item));
    });
  }

  const dateGroups = Array.from(map.entries())
    .filter(([k]) => /^\d{4}-\d{2}-\d{2}/.test(k))
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime());

  dateGroups.forEach(([key, items]) => {
    result.push(getRequestSupplyDateTypeItem(key, getSupplyFormattedDate('DD.MM.YYYY, dddd', key), items));
    items.forEach((item) => {
      result.push(getRequestSupplyDataTypeItem(key, item));
    });
  });

  if (map.has(ERequestSupplyGroupKey.Delivered)) {
    const items = map.get(ERequestSupplyGroupKey.Delivered);

    result.push(getRequestSupplyDateTypeItem(ERequestSupplyGroupKey.Delivered, 'Заказ доставлен', items));
    items?.forEach((item) => {
      result.push(getRequestSupplyDataTypeItem(ERequestSupplyGroupKey.Delivered, item));
    });
  }

  if (map.has(ERequestSupplyGroupKey.Cancelled)) {
    const items = map.get(ERequestSupplyGroupKey.Cancelled);

    result.push(getRequestSupplyDateTypeItem(ERequestSupplyGroupKey.Cancelled, 'Не согласовано', items));
    items?.forEach((item) => {
      result.push(getRequestSupplyDataTypeItem(ERequestSupplyGroupKey.Cancelled, item));
    });
  }

  return result;
};

export const mapRequestMembers = (members: IRequestMembers | null) => {
  if (!members) {
    return [];
  }

  const contacts = (email?: string | null, phone?: string) => {
    return phone ? `${phoneFormatter(phone)} • ${getEmail(email)}` : getEmail(email);
  };

  const items = [
    {
      name: getUserName.fullName(members.user.lastName, members.user.firstName, members.user.patronymic),
      role: EUserRole.User,
      contacts: contacts(members.user.email, members.user.phone),
    },
  ];

  if (members.manager) {
    items.push({
      name: getUserName.fullName(members.manager.lastName, members.manager.firstName, members.manager.patronymic),
      role: EUserRole.Manager,
      contacts: contacts(members.manager.email, members.manager.phone),
    });
  }

  if (members.sellerManagers.length) {
    const sellerManagers = members.sellerManagers.map((item) => {
      return {
        name: getUserName.fullName(item.lastName, item.firstName, item.patronymic),
        role: EUserRole.SellerManager,
        contacts: contacts(item.email, item.phone),
      };
    });

    items.push(...sellerManagers);
  }

  return items;
};

export const calculateUnassignedInventoriesCount = (distributions: IRequestDistribution[]): number => {
  return distributions.reduce((acc: number, item) => acc + item.unassignedCount, 0);
};

export const renderRequestListRecords = (role: string, navigate: (id: number) => void, account?: IAccountShort) => {
  const asSellerManager = role === EUserRole.SellerManager;

  const records = [
    {
      key: 'id',
      render: (_, record) => {
        const requestId = getRequestIdForView(record.id, record.createdAt);

        return (
          <div>
            <div className="flex align-center">
              {record.isNew && <div className="mr-2 new-indicator" />}

              <div className="ff-medium fs-s ellipsis" title={requestId}>
                {requestId}
              </div>
            </div>

            <span className="ff-regular fs-xs color-dark-grey ellipsis" title={record.legal.name}>
              {record.legal.name}
            </span>
          </div>
        );
      },
      width: 165,
    },
    {
      key: 'progress',
      render: (_, record) => {
        return (
          <div>
            <div className="flex align-center justify-between">
              <span className="ff-medium fs-s">Обработано</span>

              <span className="ff-regular fs-xs color-dark-grey">{`${record.percentageCompleted}%`}</span>
            </div>

            <Progress
              percent={record.percentageCompleted}
              showInfo={false}
              strokeColor={
                record.percentageCompleted === PERCENT_100
                  ? '#C8CCD0'
                  : record.percentageCompleted < PERCENT_50
                    ? '#F6C543'
                    : '#69A443'
              }
            />
          </div>
        );
      },
      width: 240,
    },
    {
      key: 'user',
      render: (_, record) => {
        if (!record.user) {
          return null;
        }

        const userName = getUserName.fullName(record.user.lastName, record.user.firstName, record.user.patronymic);
        let subdivisionName = '';

        if (record.subdivision) {
          if (asSellerManager) {
            subdivisionName = `${record.account.name} (${record.subdivision.name})`;
          } else {
            subdivisionName = record.subdivision.name;
          }
        }

        return (
          <div>
            <div className="ff-medium fs-s ellipsis" title={subdivisionName}>
              {subdivisionName}
            </div>

            <span className="ff-regular fs-xs color-dark-grey ellipsis" title={userName}>
              {userName}
            </span>
          </div>
        );
      },
      width: 268,
    },
    {
      key: 'price',
      render: (_, record) => {
        const isSelfRegistered = getIsSelfRegistered(account);
        const priceToDisplay = getPriceToDisplay(record.totalPrice, record.totalPriceWithTaxes, account);

        return (
          <div>
            <div>
              <div className="ff-medium fs-s ellipsis" title={priceFormatter(priceToDisplay)}>
                {priceFormatter(priceToDisplay)}
              </div>
            </div>

            <span className="ellipsis" title={priceFormatter(record.totalPriceWithTaxes)}>
              <span className="ff-regular fs-xs color-dark-grey">Сумма с НДС</span>
              {!isSelfRegistered && (
                <span className="ff-regular fs-xs color-dark-grey">{` ${priceFormatter(record.totalPriceWithTaxes)}`}</span>
              )}
            </span>
          </div>
        );
      },
      width: 240,
    },
    {
      key: 'comment',
      render: (_, record) => {
        if (!record.comment) {
          return null;
        }

        return (
          <Paragraph
            ellipsis={{ rows: 2, expandable: true }}
            classNames={{ text: 'ff-regular fs-s', toggle: 'ff-semibold fs-xs' }}
            color={{ toggle: '#387D61' }}
          >
            {record.comment}
          </Paragraph>
        );
      },
      width: 451,
    },
    {
      key: 'goods',
      render: (_, record) => {
        const images = record.firstFiveSupplies.map((supply) => supply.good.images?.[0]);
        const visibleImages = images.length > VISIBLE_IMAGES_LIMIT_4 ? images.slice(0, VISIBLE_IMAGES_LIMIT_3) : images;
        const hiddenImagesCount = record.positionsCount - visibleImages.length;

        return (
          <div className="flex align-center justify-end gap-5">
            {visibleImages.map((src, index) => (
              <Image key={index} size={44} src={src} />
            ))}

            {!!hiddenImagesCount && <div className="request-list__table-item-images-count">{`+${hiddenImagesCount}`}</div>}
          </div>
        );
      },
      onCell: () => {
        return {
          style: { paddingRight: 0 },
        };
      },
      width: 244,
    },
    {
      key: 'action',
      render: (_, record) => {
        return <Button className="button-circle secondary" icon={<ArrowRightLong />} onClick={() => navigate(record.id)} />;
      },
      width: 72,
    },
  ] as ColumnsType<IRequestListItem>;

  // @ts-ignore
  return records.filter((record) => !record.hidden);
};

export const renderRequestSupplyListRecords = (
  isViewMode: boolean,
  request: IRequest,
  isSeller = false,
  user: IUser | null,
  loading: boolean,
  onStatusChange: (id: number, value: ESupplyStatus) => void,
  onAssignDeliveryDateClick: (record: ISupplyShort) => void,
  getStatusColumnFilterProps: () => TableColumnType<IRequestSupplyGroupItem>,
) => {
  const { isAccountAdmin, isSellerManager, isManager } = getUserRole(user?.roles);
  const { isCreatedRequest } = getRequestStatus(request.status);
  const accountMaxGoodPrice = request.account.maxGoodPrice;
  const subdivisionMaxGoodPrice = request.subdivision.maxGoodPrice;

  return [
    {
      key: 'name',
      title: 'Название позиции',
      render: (_, record) => {
        if (record.type === ERequestSupplyGroupType.Date) {
          return null;
        }

        const supply = record.data as ISupplyShort;

        return (
          <div className="request-supply-list__table-item-name">
            <Image size={60} src={supply.good?.images?.[0]} />

            <div style={{ flex: 1 }}>
              <div className="text-controls-accent mb-1 ellipsis" title={supply.good?.name}>
                {supply.good?.name}
              </div>

              <div className="request-supply-list__table-item-name-container">
                <Code sellerCode={supply.good?.sellerCode} />

                <Code buyerCode={supply.good?.buyerCode} />
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'count',
      title: 'Количество',
      render: (_, record) => {
        if (record.type === ERequestSupplyGroupType.Date) {
          return null;
        }

        const supply = record.data as ISupplyShort;
        const priceLimitExceeded = subdivisionMaxGoodPrice
          ? supply.price > subdivisionMaxGoodPrice
          : accountMaxGoodPrice
            ? supply.price > accountMaxGoodPrice
            : false;
        const priceToDisplay = getPriceToDisplay(supply.price, supply.priceWithTaxes, user?.account);

        return (
          <div>
            <div className="text-controls-accent mb-1 ellipsis" title={countFormatter(supply.count)}>
              {countFormatter(supply.count)}
            </div>

            <div className="request-supply-list__table-item-count-container">
              <span className="text-tag color-dark-grey ellipsis" title={`${priceFormatter(priceToDisplay)}/ед.`}>
                {`${priceFormatter(priceToDisplay)}/ед.`}
              </span>

              {isCreatedRequest && priceLimitExceeded && (
                <Tooltip
                  overlayClassName="request-supply-list__table-item-count-tooltip"
                  title="Стоимость товара превышает установленный лимит"
                >
                  <InfoRed className="icon-info-red" />
                </Tooltip>
              )}

              {supply.priceChanged && (
                <Tooltip
                  overlayClassName="request-supply-list__table-item-count-tooltip"
                  title="Цена за единицу товара была скорректирована поставщиком"
                >
                  <InfoGrey className="icon-info-grey" />
                </Tooltip>
              )}
            </div>
          </div>
        );
      },
      width: 186,
    },
    {
      key: 'amount',
      title: 'Сумма',
      render: (_, record) => {
        if (record.type === ERequestSupplyGroupType.Date) {
          return null;
        }

        const supply = record.data as ISupplyShort;
        const isSelfRegistered = getIsSelfRegistered(user?.account);
        const priceToDisplay = getPriceToDisplay(supply.price, supply.priceWithTaxes, user?.account);

        return (
          <div>
            <div className="text-controls-accent mb-1 ellipsis" title={priceFormatter(supply.count * priceToDisplay)}>
              {priceFormatter(supply.count * priceToDisplay)}
            </div>

            <span className="ellipsis" title={priceFormatter(supply.count * supply.priceWithTaxes)}>
              <span className="ff-regular fs-xs color-dark-grey">Сумма с НДС</span>
              {!isSelfRegistered && (
                <span className="ff-regular fs-xs color-dark-grey">{` ${priceFormatter(supply.count * supply.priceWithTaxes)}`}</span>
              )}
            </span>
          </div>
        );
      },
      width: 216,
    },
    {
      key: 'status',
      ...getStatusColumnFilterProps(),
      render: (_, record) => {
        if (record.type === ERequestSupplyGroupType.Date) {
          return null;
        }

        const supply = record.data as ISupplyShort;
        const { isCreatedSupply, isProcessingSupply } = getSupplyStatus(supply.status);

        if (!isViewMode && isManager && isCreatedSupply) {
          return (
            <div className="request-supply-list__table-item-status-container">
              <Button
                className="button-sm secondary"
                loading={loading}
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(supply.id, ESupplyStatus.Pending);
                }}
              >
                Согласовать
              </Button>
            </div>
          );
        }

        if (isSeller && (isAccountAdmin || isSellerManager) && isProcessingSupply && !supply.deliveryDate) {
          return (
            <div className="request-supply-list__table-item-status-container">
              <Button
                className="button-sm secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  onAssignDeliveryDateClick(supply);
                }}
              >
                Назначить поставку
              </Button>
            </div>
          );
        }

        return (
          <div className="request-supply-list__table-item-status-container">
            <SupplyStatus status={supply.status} isCancelledBySeller={supply.isCancelledBySeller} />
          </div>
        );
      },
      width: 206,
    },
  ] as ColumnsType<IRequestSupplyGroupItem>;
};
